import appConfig from '../../config/appConfig';
import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import {setIsYapForceCheckRequest, setYapSocketAlive} from '../../state/ducks/yapEncrypted';
import {selectGamAuthToken, selectHolderGamAuthToken} from '../../state/selectors/auth';
import {selectIsHolderConnected} from '../../state/selectors/iotDevice';
import {makeSelectAssetIdByType, makeSelectIsSocketAlive} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import backendService from '../app/backendService';
import GamClient from '../gam/gamClient';
import gamWebSocketResponseService from '../gam/gamWebSocketResponseService';
import uamClientService from '../uam/uamClientService';
import uamWebSocketResponseService from '../uam/uamWebSocketResponseService';
import yapCheckStatusService from '../yap/yapCheckStatusService';
import YapDeviceWebSocketClient from '../yap/yapDeviceWebSocketClient';
import YapHolderWebSocketClient from '../yap/yapHolderWebSocketClient';

let uamDevicePingTimeout;
let uamHolderPingTimeout;
const clearPingTimeout = (isHolder) => clearTimeout(isHolder ? uamHolderPingTimeout : uamDevicePingTimeout);

let yapDeviceReconnectTimeout;
let yapHolderReconnectTimeout;
const clearReconnectTimeout = (isHolder) =>
    clearTimeout(isHolder ? yapHolderReconnectTimeout : yapDeviceReconnectTimeout);

let isYapSocketClosed = true;

const getYapWebSocketClient = (isHolder) => (assetId, createNew, options) => {
    const Client = isHolder ? YapHolderWebSocketClient : YapDeviceWebSocketClient;

    return new Client(assetId, createNew, options);
};

const initWebSocket = async (isHolder, isReconnect) => {
    if (isReconnect && isYapSocketClosed) return;

    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    if (!assetId) return;

    if (isHolder) {
        const isHolderConnected = selectIsHolderConnected(getState());

        if (!isHolderConnected) return;
    }

    isYapSocketClosed = false;
    const isGamBackend = backendService.isGamBackend();

    clearReconnectTimeout(isHolder);

    if (isYapWebSocketConnected(isHolder)) return;

    yapCheckStatusService.createCheckStatusScheduler(isHolder);

    const onError = (definedTimer) => {
        if (isReconnect && isYapSocketClosed) return;

        disconnectYapWebSocket(!!isHolder, false);

        const defaultTimer = isGamBackend ? gamTimeouts.GAM_WS_RECONNECT_TIMEOUT : uamTimeouts.UAM_WS_RECONNECT_TIMEOUT;
        const timer = definedTimer === undefined ? defaultTimer : definedTimer;
        const timeout = setTimeout(() => initWebSocket(isHolder, true), timer * 1000);

        if (isHolder) {
            yapHolderReconnectTimeout = timeout;
        } else {
            yapDeviceReconnectTimeout = timeout;
        }
    };

    const topicSubscribeSettings = await getTopicSubscribeSettings(assetId, isHolder, isGamBackend);

    if (!topicSubscribeSettings) return onError();

    if (isReconnect && isYapSocketClosed) return;

    const yapClient = getYapWebSocketClient(isHolder)(assetId, true, topicSubscribeSettings);

    yapClient.attachCloseHandler(async (message) => {
        switch (message?.code) {
            case 1000:
                //normal close from code
                break;
            case 1003:
                if (isGamBackend) {
                    //token is expired
                    await new GamClient().refreshToken();
                }
                onError();
                break;
            case 4000:
                //fast reconnect
                onError(0);
                break;
            default:
                onError();
                break;
        }
    });

    yapClient.attachConnectHandler(() => {
        dispatch(setIsYapForceCheckRequest(true, isHolder));
        subscribeOnMessage(isHolder, isGamBackend);
        if (!isGamBackend) {
            setupWebSocketPing(isHolder);
        }
    });
};

const setupWebSocketPing = (isHolder) => {
    if (isYapSocketClosed) return;

    clearPingTimeout(isHolder);
    const timeout = setTimeout(() => {
        const yapWebSocketClient = getYapWebSocketClient(isHolder)();

        yapWebSocketClient.ping();

        setupWebSocketPing(isHolder);
    }, uamTimeouts.UAM_WS_PING_INTERVAL * 1000);

    if (isHolder) {
        uamHolderPingTimeout = timeout;
    } else {
        uamDevicePingTimeout = timeout;
    }
};

const checkSocketAlive = async () => {
    if (isYapSocketClosed) return;

    const checkStatus = async (isHolder) => {
        if (!isYapWebSocketConnected(isHolder)) return true;

        const yapWebSocketClient = getYapWebSocketClient(isHolder)();

        dispatch(setYapSocketAlive(false, isHolder));

        yapWebSocketClient.ping();
        return await waitUntilSocketRespond(isHolder);
    };

    const isDeviceSocketAlive = await checkStatus();
    const isHolderSocketAlive = await checkStatus(true);

    if (!isDeviceSocketAlive) {
        disconnectYapWebSocket(false, false);
    }
    if (!isHolderSocketAlive) {
        disconnectYapWebSocket(true, false);
    }
};

const waitUntilSocketRespond = async (isHolder, timer = uamTimeouts.UAM_WS_PING_ALIVE_INTERVAL) => {
    const state = getState();
    const isYapSocketAlive = makeSelectIsSocketAlive(isHolder)(state);

    if (isYapSocketAlive) return true;

    if (--timer < 0) {
        return false;
    }
    await helpers.timeout(1000);
    return await waitUntilSocketRespond(isHolder, timer);
};

const getTopicSubscribeSettings = async (assetId, isHolder, isGamBackend) => {
    if (isGamBackend) {
        const state = getState();
        const gamToken = isHolder ? selectHolderGamAuthToken(state) : selectGamAuthToken(state);
        const wssUrl = `${appConfig.getGamWssUrl()}?token=${gamToken}`;

        return {url: wssUrl};
    }
    return await uamClientService.assetSubscribe(assetId);
};

const disconnectYapWebSocket = (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        isYapSocketClosed = true;
        yapCheckStatusService.stopCheckStatusScheduler(isHolder);
    }

    clearReconnectTimeout(isHolder);

    let yapWebSocketClient;

    if (isHolder === undefined) {
        yapWebSocketClient = new YapDeviceWebSocketClient();
        yapWebSocketClient.disconnectClient();
        yapWebSocketClient = new YapHolderWebSocketClient();
        yapWebSocketClient.disconnectClient();
    } else {
        yapWebSocketClient = getYapWebSocketClient(isHolder)();
        yapWebSocketClient.disconnectClient();
    }
};

const reconnectYapWebSockets = () => {
    const yapDeviceWebSocketClient = new YapDeviceWebSocketClient();
    const yapHolderWebSocketClient = new YapHolderWebSocketClient();

    yapDeviceWebSocketClient.disconnectWithReconnectClient();
    yapHolderWebSocketClient.disconnectWithReconnectClient();
};

const subscribeOnMessage = (isHolder, isGamBackend) => {
    getYapWebSocketClient(isHolder)().attachMessageHandler(async (message) => {
        isGamBackend
            ? await gamWebSocketResponseService.proceedMessage(message, isHolder)
            : await uamWebSocketResponseService.proceedMessage(message, isHolder);
    });
};

const isYapWebSocketConnected = (isHolder) => {
    const assetId = makeSelectAssetIdByType(isHolder)(getState());

    return getYapWebSocketClient(isHolder)().isConnectedCheck(assetId);
};

export default {
    initWebSocket,
    disconnectYapWebSocket,
    reconnectYapWebSockets,
    isYapWebSocketConnected,
    checkSocketAlive,
};
